/**
 * Komponenta za prikaz grafikona nadmorske visine i brzine na temelju GPS podataka.
 * Prikazuje dva linijska grafikona: jedan za nadmorsku visinu, drugi za brzinu tijekom vožnje.
 */
import { Component, input, inject, OnInit, signal } from '@angular/core';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { Route } from '@app/shared/models/ride-history/route.model';
import { Gps } from '@app/shared/models/ride-history/gps.model';
import { HttpService } from '../../../service/http/http.service';
import { environment } from '@environments/environment';
import { backendRestApis } from '@environments/backend-rest-apis';

@Component({
  selector: 'app-altitude-and-speed-graph',
  imports: [NgxChartsModule],
  templateUrl: './altitude-and-speed-graph.component.html',
  styleUrl: './altitude-and-speed-graph.component.css'
})

export class AltitudeAndSpeedGraphComponent implements OnInit {
  private httpService = inject(HttpService);

  /**
   * Ulazni podaci - popis ruta za koje se dohvaćaju GPS podaci.
   */
  routes = input.required<Route[] | undefined>();

  /**
   * Signal koji sadrži sve dohvaćene GPS podatke.
   */
  gps = signal<Gps[]>([]);

  /**
   * Podaci za grafikon nadmorske visine.
   */
  altitudeData = signal<any[]>([]);

  /**
   * Podaci za grafikon brzine.
   */
  speedData = signal<any[]>([]);

  /**
   * Formatira vrijednosti brzine za prikaz na y-osi grafikona.
   */
  formatSpeedTick = (value: number): string => `${value.toFixed(1)} km/h`;

  /**
   * Formatira vrijednosti nadmorske visine za prikaz na y-osi grafikona.
   */
  formatAltitudeTick = (value: number): string => `${value} m`;

  /**
   * Prikazuje oznaku na x-osi svakih 5 točaka.
   */
  formatXTicks = (_: string, index: number): string => {
    return index % 5 === 0 ? _ : '';
  };

  /**
   * Popis ID-eva ruta za dohvat GPS podataka.
   */
  routeId: number[] = [];

  /**
   * Inicijalizira komponentu i dohvaća GPS podatke za sve rute.
   */
  ngOnInit() {
    const inputRoutes = this.routes();
    if (inputRoutes) {
      this.routeId = inputRoutes
        .filter((route) => route?.id !== undefined)
        .map((route) => route.id!);

      for (let id of this.routeId) {
        const url = `${environment.apiUrl}${backendRestApis.gps.getAllGpsWithRouteId(id)}`;
        this.httpService.get<Gps[]>(url).subscribe({
          next: (gpsData) => {
            this.gps.update((current) => {
              const updated = [...current, ...gpsData];

              const sorted = updated.sort(
                (a, b) =>
                  new Date(a.timestamp).getTime() -
                  new Date(b.timestamp).getTime()
              );

              // ALTITUDE graf
              const altitudeSeries = sorted.map((g) => ({
                name: new Date(g.timestamp).toLocaleTimeString('hr-HR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                }),
                value: g.altitude,
              }));

              this.altitudeData.set([
                {
                  name: 'Nadmorska visina',
                  series: altitudeSeries,
                },
              ]);

              // SPEED graf
              const speedSeries = sorted.map((g) => ({
                name: new Date(g.timestamp).toLocaleTimeString('hr-HR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                }),
                value: g.speed*3.6, //pretvaram u km/h
              }));

              this.speedData.set([
                {
                  name: 'Brzina',
                  series: speedSeries,
                },
              ]);

              return updated;
            });
          },
        });
      }
    }
  }
}
